<template>
	<v-container>
		<v-form ref="form" v-model="valid" @submit.prevent="sendEmissionsReport">
			<div v-for="(question, index) in questions" :key="index">
				<v-row
					v-if="shouldDisplay(index) && (index < 23 || index > 38 || hasDistributionPartnerReports === 'No')"
					class="mx-auto">
					<v-row no-gutters class="my-12" v-if="question.type == 'spacer'"></v-row>
					<v-row no-gutters v-else-if="question.type == 'title'" class="contentRow primary"
						:style="question.color ? `background-color: ${question.color} !important; border-color: ${question.color} !important`: '' ">
						<v-col cols="12" class="mr-auto white--text">
							<h2>{{ question.name }}</h2>
						</v-col>
						<v-col cols="12" v-if="question.intro" class="px-4 pb-4">
							<v-card class="methodology-intro elevation-4">
								<div class="methodology-intro-accent" />
								<v-card-text class="pa-5 pt-4">

									<p
										class="methodology-intro-text mb-4"
										v-html="formatIntroText(question.intro)"
									/>

									<v-divider class="methodology-divider mb-3" />

									<v-row dense class="methodology-categories">
										<v-col
											v-for="category in methodologyCategories"
											:key="category.label"
											cols="12"
											sm="6"
											md="2"
											class="py-1"
										>
											<div class="methodology-category-item">
												<v-icon small :color="category.color" class="mr-2">
													{{ category.icon }}
												</v-icon>
												<span>{{ category.label }}</span>
											</div>
										</v-col>
									</v-row>
								</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<v-btn class="my-3" dark text v-if="!!question.hint" @click.stop="toggleHint(index)">
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>
						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3 white--text">
								<v-card-text style="white-space: pre-wrap">
									{{ question.hint }}
								</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right white--text text-caption size-1">
								<i style="font-size: 12px">(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
					<v-row v-else class="contentRow" align="center" no-gutters>
						<v-col cols="12">
							<h3 class="my-3">
								{{ question.name }}
								<sup v-if="!!question.required">
									<v-icon color="error" x-small right>fas fa-asterisk</v-icon>
								</sup>
							</h3>
						</v-col>
						<!-- Render inputs based on question type -->
						<v-col cols="12" v-if="question.type == 'singleanswer'">
							<v-text-field dense align="center" label="Answer" hide-details="auto"
								placeholder="Enter your answer:"
								:rules=" question.required ? notEmptyRules.concat(numberRule) : numberRule " outlined
								v-model="answers[index]" class="my-3"></v-text-field>
						</v-col>
						<v-col cols="12" v-if="question.type == 'percentageanswer'">
							<v-text-field dense align="center" label="Answer" hide-details="auto"
								placeholder="Enter your answer (0-100):"
								:rules=" question.required ? notEmptyRules.concat(numberRule.concat(percentageRule)) : numberRule.concat(percentageRule) "
								outlined v-model="answers[index]" class="my-3"></v-text-field>
						</v-col>
						<v-col cols="12" v-if="question.type == 'percentageshippingin'">
							<v-text-field dense align="center" label="Answer" hide-details="auto"
								placeholder="Enter your answer (0-100):"
								:rules=" question.required ? notEmptyRules.concat(numberRule.concat(percentageRuleIn)) : numberRule.concat(percentageRuleIn) "
								outlined v-model="answers[index]" class="my-3"></v-text-field>
						</v-col>
						<v-col cols="12" v-if="question.type == 'percentageshippingout'">
							<v-text-field dense align="center" label="Answer" hide-details="auto"
								placeholder="Enter your answer (0-100):"
								:rules=" question.required ? notEmptyRules.concat(numberRule.concat(percentageRuleOut)) : numberRule.concat(percentageRuleOut) "
								outlined v-model="answers[index]" class="my-3"></v-text-field>
						</v-col>
						<v-col cols="12" v-if="question.type == 'multiplechoice'">
							<v-select dense outlined v-model="answers[index]" :items="question.possibilities"
								menu-props="auto" label="Please Choose" hide-details="auto" single-line
								:rules="question.required ? notEmptyRules : []" class="my-3"></v-select>
						</v-col>
						<v-col cols="12">
							<v-btn class="my-3" text v-if="!!question.hint" @click.stop="toggleHint(index)">
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>
						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3">
								<v-card-text style="white-space: pre-wrap">
									{{ question.hint }}
								</v-card-text>
							</v-card>
						</v-col>
						<v-col cols="12">
							<p class="float-right grey--text text-caption">
								<i style="font-size: 12px">(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>
				</v-row>

				<!-- Insert Part 5 section after index 22 -->
				<template v-if="index === 22">
					<!-- Title with color styling -->
					<v-row no-gutters class="contentRow primary"
						:style="`background-color: rgb(30,154,116) !important; border-color: rgb(30,154,116) !important;`">
						<v-col cols="6" class="mr-auto white--text">
							<h2>Part 3: Ingoing & Outgoing Shipping</h2>
						</v-col>
						<v-col cols="12">
							<p class="float-right white--text text-caption size-1">
								<i style="font-size: 12px">(Question ID {{ index }})</i>
							</p>
						</v-col>
					</v-row>

					<!-- Dropdown for Distribution Partner Reports -->
					<v-row class="contentRow" align="center" no-gutters>
						<v-col cols="12">
							<br />
							<h3>
								Do you have Reports from Distribution Partners?
								<sup>
									<v-icon color="error" x-small right>fas fa-asterisk</v-icon>
								</sup>
							</h3>
						</v-col>
						<v-col cols="12">
							<v-select dense outlined v-model="hasDistributionPartnerReports" :items="['Yes', 'No']"
								menu-props="auto" label="Please Choose" hide-details="auto" single-line></v-select>
						</v-col>
						<v-col cols="12">
							<v-btn class="my-3" text v-if="!!question.hint" @click.stop="toggleHint(index)">
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>
						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3">
								<v-card-text style="white-space: pre-wrap">
									If 'Yes' is selected, you will be asked to provide the company name and CO2 emission
									for each distribution partner.
								</v-card-text>
							</v-card>
						</v-col>
					</v-row>
					<!-- Partner Reports Table (Only if 'Yes' is selected) -->
					<v-row class="contentRow" no-gutters v-if="hasDistributionPartnerReports === 'Yes'">
						<v-col cols="12">
							<br />
							<h3>
								What are the companies and their annual CO2 emissions of thoese distribution partners?
								<sup>
									<v-icon color="error" x-small right>fas fa-asterisk</v-icon>
								</sup>
							</h3>
						</v-col>
						<v-col cols="12">
							<v-simple-table>
								<thead>
									<tr>
										<th class="text-left">Company</th>
										<th class="text-left">CO2 Emission (in Kg)</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(partner, pIndex) in distributionPartners" :key="pIndex">
										<td>
											<v-text-field dense outlined v-model="partner.company"
												placeholder="Enter company name" hide-details></v-text-field>
										</td>
										<td>
											<v-text-field dense outlined v-model="partner.co2Emission"
												placeholder="Enter CO2 emission" type="number"
												hide-details></v-text-field>
										</td>
										<td>
											<v-btn icon color="red" @click="removeDistributionPartnerRow(pIndex)">
												<v-icon small color="red">fas fa-times</v-icon>
											</v-btn>
										</td>
									</tr>
								</tbody>
							</v-simple-table>
							<v-btn color="primary" @click="addDistributionPartnerRow" class="mt-3">
								Add Row
							</v-btn>
						</v-col>
					</v-row>
					<v-row class="contentRow" align="center" no-gutters v-if="hasDistributionPartnerReports === 'Yes'">
						<v-col cols="12">
							<br />
							<h3>
								What percentage of the facility's total shipments are outgoing shipping?

							</h3>
						</v-col>
						<v-col cols="12">
							<v-text-field dense align="center" hide-details="auto" placeholder="Enter your answer:"
								outlined type="number" min="0" max="100" v-model="percentage" class="my-3"
								:rules="[v => (v === null || (v >= 0 && v <= 100)) || 'Enter a number between 0 and 100']"></v-text-field>
						</v-col>
						<v-col cols="12">
							<v-btn class="my-3" text v-if="!!question.hint" @click.stop="toggleHint(index)">
								{{ !!question.hintVisible ? "Hide" : "Show" }} Question Details
								<v-icon right>far fa-question-circle</v-icon>
							</v-btn>
						</v-col>
						<v-col cols="12" v-if="!!question.hintVisible">
							<v-card outlined class="my-3">
								<v-card-text style="white-space: pre-wrap">
									Your answer should be a percentage value between 0-100. If left blank, it will be
									calculated automatically as 50%.
								</v-card-text>
							</v-card>
						</v-col>
					</v-row>
				</template>

				<!-- Part 5: Packaging material table -->
				<template v-if="index === 50 && answers[48] === 'Yes'">
					<v-row class="contentRow packaging-section" no-gutters>
						<v-col cols="12">
							<h3 class="my-3">
								Packaging materials used annually
								<sup><v-icon color="error" x-small right>fas fa-asterisk</v-icon></sup>
							</h3>
							<p class="grey--text text-body-2 mb-2">
								Select the materials you use, then enter details for each one below.
							</p>
						</v-col>

						<v-col cols="12">
							<p class="text-subtitle-2 mb-2">Which packaging materials do you use?</p>

							<div
								v-for="category in packagingCategories"
								:key="category.id"
								class="packaging-category-block"
							>
								<v-checkbox
									v-model="selectedPackagingCategories"
									:value="category.id"
									:label="category.label"
									hide-details
									dense
									class="mt-0 mb-0"
									@change="onPackagingCategoryChange"
								/>

								<template v-if="selectedPackagingCategories.includes(category.id)">
									<v-card
										v-if="category.id === 'plastic'"
										outlined
										class="packaging-plastic-picker pa-3 mb-4 mt-2"
									>
										<p class="text-subtitle-2 mb-2">Which plastic types do you use?</p>
										<div
											v-for="plastic in plasticMaterials"
											:key="plastic.material"
											class="packaging-plastic-block"
										>
											<v-checkbox
												v-model="selectedPlasticMaterials"
												:value="plastic.material"
												:label="plastic.label"
												hide-details
												dense
												class="mt-0 mb-0"
												@change="onPlasticMaterialChange"
											/>
											<v-card
												v-if="selectedPlasticMaterials.includes(plastic.material)"
												outlined
												class="mb-3 packaging-material-card mt-2"
											>
												<v-card-title class="packaging-material-title py-3">
													<span>{{ plastic.material }}</span>
												</v-card-title>
												<v-card-text class="pt-0 pb-4">
													<packaging-material-fields
														:row="getPackagingRow(plastic.material)"
														:uses-unit-weight-method="usesUnitWeightMethod"
														:end-of-life-options="endOfLifeOptions"
														:format-row-weight="formatPackagingRowWeight"
														:show-end-of-life="showRowEndOfLife"
													/>
												</v-card-text>
											</v-card>
										</div>
									</v-card>

									<template v-else>
										<v-card
											v-for="row in getPackagingRowsForCategory(category)"
											:key="row.material"
											outlined
											class="mb-4 packaging-material-card mt-2"
										>
											<v-card-title class="packaging-material-title py-3">
												<span>{{ row.material }}</span>
											</v-card-title>
											<v-card-text class="pt-0 pb-4">
												<packaging-material-fields
													:row="row"
													:uses-unit-weight-method="usesUnitWeightMethod"
													:end-of-life-options="endOfLifeOptions"
													:format-row-weight="formatPackagingRowWeight"
													:show-end-of-life="showRowEndOfLife"
												/>
											</v-card-text>
										</v-card>
									</template>
								</template>
							</div>
						</v-col>
					</v-row>
				</template>
			</div>

			<!-- Submission Section -->
			<v-row no-gutters class="contentRow" style="border: 2px solid #0b7078">
				<v-col cols="12" class="mr-auto">
					<h2>Ready to submit?</h2>
					<p>Note: You will not be able to change your answers later.</p>
					<v-btn color="primary" :disabled="disableSubmit" type="submit" class="mt-4">
						Submit
						<v-icon right>fas fa-paper-plane</v-icon>
					</v-btn>
				</v-col>
			</v-row>
		</v-form>
	</v-container>
</template>

<script>
import { calculatePackagingRowEmission } from "../utils/packagingEmission"
import PackagingMaterialFields from "./PackagingMaterialFields.vue"

const PACKAGING_CATEGORIES = [
	{ id: "cardboard", label: "Cardboard / Paper", materials: ["Cardboard"] },
	{ id: "plastic", label: "Plastic", materials: ["PET", "HDPE", "LDPE", "PP", "PVC/Other"] },
	{ id: "glass", label: "Glass", materials: ["Glass"] },
	{ id: "aluminum", label: "Aluminum", materials: ["Aluminum"] },
]

const PLASTIC_MATERIALS = [
	{ material: "PET", label: "PET (bottles)" },
	{ material: "HDPE", label: "HDPE (containers, bottles)" },
	{ material: "LDPE", label: "LDPE (bags, film)" },
	{ material: "PP", label: "PP (caps, straws)" },
	{ material: "PVC/Other", label: "PVC / Other plastics" },
]

const PLASTIC_MATERIAL_NAMES = PLASTIC_MATERIALS.map(({ material }) => material)

export default {
	name: "FormInputEmissions",
	components: {
		PackagingMaterialFields,
	},
	data() {
		const emptyPackagingRow = (material) => ({
			material,
			totalWeightKg: "",
			unitWeightKg: "",
			quantity: "",
			recycledContentPct: "",
			recycledAfterUsePct: "",
			endOfLife: "",
		})
		return {
			valid: false,
			answers: [],
			hasDistributionPartnerReports: "",
			distributionPartners: [{ company: "", co2Emission: "" }],
			percentage: null,
			endOfLifeOptions: [
				"Landfill",
				"Incineration (no energy recovery)",
				"Incineration (with energy recovery)",
			],
			packagingMaterials: [
				emptyPackagingRow("Cardboard"),
				emptyPackagingRow("PET"),
				emptyPackagingRow("HDPE"),
				emptyPackagingRow("LDPE"),
				emptyPackagingRow("PP"),
				emptyPackagingRow("PVC/Other"),
				emptyPackagingRow("Glass"),
				emptyPackagingRow("Aluminum"),
			],
			packagingCategories: PACKAGING_CATEGORIES,
			plasticMaterials: PLASTIC_MATERIALS,
			selectedPackagingCategories: [],
			selectedPlasticMaterials: [],
			methodologyCategories: [
				{ label: "Energy Consumption", icon: "fas fa-bolt", color: "#e9891b" },
				{ label: "Business Trip", icon: "fas fa-plane", color: "#cd1c42" },
				{ label: "Ingoing & Outgoing Shipping", icon: "fas fa-shipping-fast", color: "#6772bc" },
				{ label: "Facility Organization", icon: "fas fa-building", color: "#1e9a74" },
				{ label: "Packaging", icon: "fas fa-box", color: "#7b1fa2" },
			],
			notEmptyRules: [(v) => !!v || "This field cannot be empty"],
			numberRule: [
				(v) => {
					if (!isNaN(parseInt(v, 10))) return true
					return "This field requires an integer input"
				},
			],
			percentageRule: [
				(v) =>
					(v <= 100 && v >= 0) || "Percentage value has to be between 0-100",
			],
			percentageRuleIn: [
				(v) => {
					if (this.checkTotalPercentage(v)) return true
					return "Total percentage of shipping methods cannot exceed 100%"
				},
			],
			percentageRuleOut: [
				(v) => {
					if (this.checkTotalPercentageOut(v)) return true
					return "Total percentage of shipping methods cannot exceed 100%"
				},
			],
		}
	},
	props: {
		preview: Boolean,
		previewID: Number,
		questions: Array,
		id: Number,
		name: String,
	},
	computed: {
		usesUnitWeightMethod() {
			return this.answers[49] === "Unit weight × quantity"
		},
		packagingPayload() {
			return {
				weightEntryMethod: this.answers[49] || "Total weight per material (kg/year)",
				defaultEndOfLife: this.answers[50] || "Mostly landfilled",
				materials: this.activePackagingMaterialRows,
			}
		},
		isPlasticCategorySelected() {
			return this.selectedPackagingCategories.includes("plastic")
		},
		selectedPackagingMaterialNames() {
			const names = []
			for (const category of PACKAGING_CATEGORIES) {
				if (!this.selectedPackagingCategories.includes(category.id)) {
					continue
				}
				if (category.id === "plastic") {
					names.push(...this.selectedPlasticMaterials)
					continue
				}
				names.push(...category.materials)
			}
			return names
		},
		activePackagingMaterialRows() {
			return this.packagingMaterials.filter((row) =>
				this.selectedPackagingMaterialNames.includes(row.material)
			)
		},
		plasticPackagingMaterialRows() {
			return this.packagingMaterials.filter(
				(row) =>
					PLASTIC_MATERIAL_NAMES.includes(row.material) &&
					this.selectedPlasticMaterials.includes(row.material)
			)
		},
		disableSubmit() {
			// Disable if the v-form is invalid or preview mode is on
			if (this.preview || !this.valid) return true
			// Disable if the Reports from Distribution Partners question is not answered
			if (!this.hasDistributionPartnerReports) return true
			// If "Yes" is selected, check that every distribution partner row has both fields filled
			if (this.hasDistributionPartnerReports === "Yes") {
				for (let partner of this.distributionPartners) {
					if (!partner.company || !partner.co2Emission) return true
				}
			}
			if (this.answers[48] === "Yes" && !this.answers[49]) {
				return true
			}
			if (this.answers[48] === "Yes") {
				if (this.selectedPackagingCategories.length === 0) return true
				if (this.isPlasticCategorySelected && this.selectedPlasticMaterials.length === 0) {
					return true
				}
			}
			return false
		}
	},
	methods: {
		formatIntroText(intro) {
			if (!intro) return ""
			const standards = ["GHG Protocol", "ISO 14064", "CSRD", "ESRS", "VSME"]
			let html = String(intro)
				.replace(/&/g, "&amp;")
				.replace(/</g, "&lt;")
				.replace(/>/g, "&gt;")
			for (const term of standards) {
				const pattern = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
				html = html.replace(
					new RegExp(pattern, "g"),
					`<strong class="methodology-standard">${term}</strong>`
				)
			}
			return html
		},
		shouldDisplay(index) {
			if (!this.questions[index].dependencyQuestionID) {
				return true
			}
			const dependencyIndex = this.questions[index].dependencyQuestionID
			const answer = this.answers[dependencyIndex]
			if (!answer) return false
			if (this.questions[index].dependencyQuestionValue.includes(answer))
				return true
			if (Array.isArray(answer)) {
				return answer.some((element) =>
					this.questions[index].dependencyQuestionValue.includes(element)
				)
			}
			return false
		},
		removeDistributionPartnerRow(index) {
			this.distributionPartners.splice(index, 1)
		},
		showRowEndOfLife(row) {
			const recycledAfterUse = parseFloat(row.recycledAfterUsePct)
			return !Number.isFinite(recycledAfterUse) || recycledAfterUse < 100
		},
		getPackagingRow(material) {
			return this.packagingMaterials.find((row) => row.material === material)
		},
		getPackagingRowsForCategory(category) {
			if (category.id === "plastic") {
				return this.plasticPackagingMaterialRows
			}
			return this.packagingMaterials.filter((row) =>
				category.materials.includes(row.material)
			)
		},
		clearPackagingRow(material) {
			const row = this.getPackagingRow(material)
			if (!row) return
			row.totalWeightKg = ""
			row.unitWeightKg = ""
			row.quantity = ""
			row.recycledContentPct = ""
			row.recycledAfterUsePct = ""
			row.endOfLife = ""
		},
		onPackagingCategoryChange() {
			for (const category of PACKAGING_CATEGORIES) {
				if (this.selectedPackagingCategories.includes(category.id)) {
					continue
				}
				category.materials.forEach((material) => this.clearPackagingRow(material))
				if (category.id === "plastic") {
					this.selectedPlasticMaterials = []
				}
			}
		},
		onPlasticMaterialChange() {
			PLASTIC_MATERIALS.forEach(({ material }) => {
				if (!this.selectedPlasticMaterials.includes(material)) {
					this.clearPackagingRow(material)
				}
			})
		},
		formatPackagingRowWeight(row) {
			if (!this.usesUnitWeightMethod) {
				return "-"
			}
			const weight = calculatePackagingRowEmission(row, this.packagingPayload).totalWeightKg
			return weight > 0 ? `${weight.toFixed(2)} kg` : "-"
		},
		sendEmissionsReport() {
			const finalPercentage = (this.percentage === null) ? 50 : this.percentage
			const reportData = {
				answers: this.answers,
				distributionPartners:
					this.hasDistributionPartnerReports === "Yes"
						? this.distributionPartners
						: [],
				percentage: finalPercentage,
				packaging: this.answers[48] === "Yes"
					? { materials: this.activePackagingMaterialRows }
					: null,
			}
			this.$emit("submitForm", reportData)
		},
		toggleHint(index) {
			let source = { hintVisible: !this.questions[index].hintVisible }
			let newQuestion = Object.assign({}, this.questions[index], source)
			this.$set(this.questions, index, newQuestion)
		},
		addDistributionPartnerRow() {
			this.distributionPartners.push({ company: "", co2Emission: "" })
		},
		checkTotalPercentage(ans) {
			if (isNaN(ans)) return true
			if (ans < 0) return false
			if (ans > 100) return false
			let sum1 = 0
			for (let i = 24; i < 28; i++) {
				if (!isNaN(this.answers[i])) {
					if (0 < this.answers[i] && this.answers[i] < 101) {
						let integer = parseFloat(this.answers[i])
						sum1 += integer
					}
				}
			}
			return sum1 <= 100
		},
		checkTotalPercentageOut(ans) {
			if (isNaN(ans)) return true
			if (ans < 0) return false
			if (ans > 100) return false
			let sum1 = 0
			for (let i = 32; i < 36; i++) {
				if (!isNaN(this.answers[i])) {
					if (0 < this.answers[i] && this.answers[i] < 101) {
						let integer = parseFloat(this.answers[i])
						sum1 += integer
					}
				}
			}
			return sum1 <= 100
		},
	},
}
</script>

<style scoped>
.packaging-category-block + .packaging-category-block {
	margin-top: 8px;
}

.packaging-plastic-block + .packaging-plastic-block {
	margin-top: 4px;
}

.packaging-plastic-picker {
	border-color: rgba(123, 31, 162, 0.25);
	background-color: rgba(123, 31, 162, 0.04);
}

.packaging-material-card {
	border-color: rgba(123, 31, 162, 0.2);
}

.packaging-material-title {
	font-size: 1rem;
	font-weight: 600;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.methodology-intro {
	position: relative;
	overflow: hidden;
	border-radius: 12px !important;
	background: linear-gradient(135deg, #ffffff 0%, #f4fbf8 100%);
	border: 1px solid rgba(30, 154, 116, 0.2) !important;
}

.methodology-intro-accent {
	position: absolute;
	top: 0;
	left: 0;
	width: 5px;
	height: 100%;
	background: linear-gradient(180deg, #1e9a74 0%, #0b7078 100%);
}

.methodology-intro-avatar {
	background: rgba(30, 154, 116, 0.12) !important;
	border: 1px solid rgba(30, 154, 116, 0.25);
}

.methodology-intro-label {
	font-size: 0.75rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #1e9a74;
}

.methodology-intro-subtitle {
	font-size: 1.05rem;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
	line-height: 1.35;
}

.methodology-intro-text {
	font-size: 0.9375rem;
	line-height: 1.65;
	color: rgba(0, 0, 0, 0.72);
	margin-bottom: 0;
}

.methodology-chip {
	background-color: rgba(30, 154, 116, 0.08) !important;
	border: 1px solid rgba(30, 154, 116, 0.22) !important;
	font-weight: 600;
}

.methodology-divider {
	border-color: rgba(30, 154, 116, 0.15) !important;
}

.methodology-category-item {
	display: flex;
	align-items: center;
	font-size: 0.8125rem;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.75);
	padding: 6px 8px;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.85);
	border: 1px solid rgba(0, 0, 0, 0.06);
	height: 100%;
}
</style>

<style>
.methodology-intro-text .methodology-standard {
	font-weight: 700 !important;
	color: rgba(0, 0, 0, 0.87);
}
</style>
